import React from 'react';

/**
 * Contrôles MAÎTRE de la timeline (carte GPS universelle).
 * 
 * Slider interactif de 0 → timelineMax (secondes depuis le début de la session).
 * Gradient coloré bleu/jaune/rouge selon les segments.
 * Les boutons agissent sur le moteur de lecture GPS.
 * 
 * PAS de volume ici — le volume est dans MediaIndicator.
 */
const MediaControls = ({
  timelineState = { value: 0, max: 0, display: '⏱ 00:00 / 00:00' },
  onPlay,
  onPause,
  onStart,
  onGoLive,
  canGoLive = false,
  onTimelineInput,
  onTimelineChange,
  idPrefix = '',
  /** Repères (Début, partage, alerte, passages, Fin) : points sur la barre et rangée cliquable. */
  markers = null,
}) => {
  const timelinePercent = timelineState.max > 0
    ? Math.min(100, (timelineState.value / timelineState.max) * 100)
    : 0;

  // Heure réelle (horloge du téléphone au moment de la capture) à la position du curseur.
  const cursorTs = Number.isFinite(timelineState.minTs) ? timelineState.minTs + (timelineState.value || 0) * 1000 : null;
  const cursorDate = cursorTs !== null ? new Date(cursorTs) : null;
  const spansDays = Number.isFinite(timelineState.maxTs) && cursorDate
    && new Date(timelineState.minTs).toDateString() !== new Date(timelineState.maxTs).toDateString();
  const cursorLabel = cursorDate
    ? `${spansDays ? `${cursorDate.toLocaleDateString('fr-FR')} ` : ''}${cursorDate.toLocaleTimeString('fr-FR')}`
    : null;

  const span = Number.isFinite(timelineState.minTs) && Number.isFinite(timelineState.maxTs)
    ? timelineState.maxTs - timelineState.minTs : 0;
  const markerPercent = (t) => (span > 0 ? Math.min(100, Math.max(0, ((t - timelineState.minTs) / span) * 100)) : 0);
  // Même décalage que le curseur (pouce de 24 px) : le point tombe sous le pouce.
  const markerLeft = (t) => { const p = markerPercent(t); return `calc(${p}% + ${(0.5 - p / 100) * 24}px)`; };
  const seekTo = (t) => onTimelineChange?.(Math.max(0, (t - timelineState.minTs) / 1000));
  const shownMarkers = span > 0 && Array.isArray(markers) ? markers : [];

  return (
    <div className="media-controls-block">
      {/* Actions de lecture */}
      <div className="button-row">
        <button id={`${idPrefix}start`} onClick={onStart}>⏮️ Début</button>
        <button id={`${idPrefix}play`} onClick={onPlay}>▶️</button>
        <button id={`${idPrefix}pause`} onClick={onPause}>⏸️</button>
        {canGoLive && (
          <button
            id={`${idPrefix}live`}
            onClick={onGoLive}
            className={timelineState.isLive ? 'live-button active' : 'live-button'}
            style={timelineState.isLive ? {
              backgroundColor: '#dc2626', color: '#ffffff', borderColor: '#ef4444',
              boxShadow: '0 0 12px rgba(239, 68, 68, 0.7)', fontWeight: 'bold'
            } : {}}
          >
            🔴 Direct
          </button>
        )}
      </div>

      <div className="timeline-stack timeline-master-stack">
        <div className="time-display" id={`${idPrefix}time-display`}>
          {timelineState.display}
        </div>
        {/* Rangée dédiée : la bulle suit le curseur sans masquer l'horloge. */}
        <div className="timeline-cursor-row">
          {cursorLabel && (
            <div
              className="timeline-cursor-time"
              id={`${idPrefix}cursor-time`}
              title="Heure réelle à la position du curseur"
              style={{
                left: `calc(${timelinePercent}% + ${(0.5 - timelinePercent / 100) * 24}px)`,
                // Aux extrémités, la bulle reste dans la barre.
                transform: `translateX(${timelinePercent < 10 ? -15 : timelinePercent > 90 ? -85 : -50}%)`,
              }}
            >
              {cursorLabel}
            </div>
          )}
        </div>
        <div className="slider-container" style={{ display: 'block', position: 'relative' }}>
          <input
            type="range"
            id={`${idPrefix}timeline`}
            className="custom-slider"
            min="0"
            max={timelineState.max ?? 0}
            value={timelineState.value || 0}
            step="0.1"
            onInput={(e) => onTimelineInput?.(parseFloat(e.target.value))}
            onChange={(e) => onTimelineChange?.(parseFloat(e.target.value))}
            style={{
              '--value-percent': `${timelinePercent}%`,
              ...(timelineState.gradient
                ? { '--timeline-gradient': timelineState.gradient }
                : {})
            }}
          />
          {shownMarkers.filter(m => !m.edge).map((m, i) => (
            <span key={`dot-${m.t}-${i}`} className="timeline-marker-dot" title={m.label}
              style={{ left: markerLeft(m.t), background: m.color }} />
          ))}
        </div>
        {shownMarkers.length > 0 && (
          <div className="timeline-marker-row" id={`${idPrefix}timeline-markers`}>
            {shownMarkers.map((m, i) => (
              <button key={`mk-${m.t}-${i}`} type="button" className="timeline-marker" style={{ color: m.color }}
                title={new Date(m.t).toLocaleTimeString('fr-FR')} onClick={() => seekTo(m.t)}>
                <span className="timeline-marker-bullet" style={{ background: m.color }} />
                {m.label}
              </button>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default MediaControls;
